import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { PackageMinus, PackagePlus } from "lucide-react";
import type { TopItem } from "@/hooks/use-analytics-data";

interface TopItemsChartProps {
  topDispensed: TopItem[];
  topReceived: TopItem[];
}

function truncateName(name: string, maxLength = 20): string {
  if (name.length <= maxLength) return name;
  return name.slice(0, maxLength) + "...";
}

function CustomTooltip({
  active,
  payload,
  color,
}: {
  active?: boolean;
  payload?: Array<{
    payload: { item_name: string; ndc: string; total_quantity: number };
  }>;
  color: string;
}) {
  if (!active || !payload?.[0]) return null;

  const item = payload[0].payload;

  return (
    <div className="rounded-lg border border-border bg-surface p-3 shadow-lg max-w-[260px]">
      <p className="text-xs font-medium text-foreground mb-1 break-words">
        {item.item_name}
      </p>
      <p className="text-[10px] text-muted-foreground mb-2 font-mono">
        NDC: {item.ndc}
      </p>
      <div className="flex items-center gap-2">
        <div
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-xs font-semibold tabular-nums">
          {item.total_quantity.toLocaleString()} units
        </span>
      </div>
    </div>
  );
}

function TopItemsBarChart({
  data,
  color,
  emptyIcon: EmptyIcon,
  emptyText,
}: {
  data: TopItem[];
  color: string;
  emptyIcon: typeof PackagePlus;
  emptyText: string;
}) {
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[280px] text-center">
        <div className="mb-3 rounded-full bg-muted p-3">
          <EmptyIcon className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">{emptyText}</p>
        <p className="text-xs text-muted-foreground mt-1">
          Data will appear as transactions are recorded
        </p>
      </div>
    );
  }

  // Format data for the bar chart
  const chartData = data.map((item) => ({
    ...item,
    displayName: truncateName(item.item_name),
  }));

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 0, right: 10, left: 0, bottom: 0 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          className="stroke-border/50"
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          className="fill-muted-foreground"
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="displayName"
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          className="fill-muted-foreground"
          width={130}
        />
        <Tooltip
          content={<CustomTooltip color={color} />}
          cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
        />
        <Bar
          dataKey="total_quantity"
          fill={color}
          radius={[0, 4, 4, 0]}
          barSize={20}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function TopItemsChart({
  topDispensed,
  topReceived,
}: TopItemsChartProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-muted/60 p-2">
              <PackageMinus className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-base">Top Dispensed Drugs</CardTitle>
              <CardDescription>
                Most dispensed by quantity in this period
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <TopItemsBarChart
            data={topDispensed}
            color="#2563eb"
            emptyIcon={PackageMinus}
            emptyText="No dispenses recorded"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-muted/60 p-2">
              <PackagePlus className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-base">Top Received Drugs</CardTitle>
              <CardDescription>
                Most received by quantity in this period
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <TopItemsBarChart
            data={topReceived}
            color="#059669"
            emptyIcon={PackagePlus}
            emptyText="No receipts recorded"
          />
        </CardContent>
      </Card>
    </div>
  );
}
